import { useState, useEffect, useCallback } from 'react';
import { Users, Merge, AlertTriangle, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import api from '@/lib/api';
import type { CrmDuplicateGroup } from '@/lib/api';

interface DedupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMergeComplete?: () => void;
}

const confidenceColors: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-yellow-100 text-yellow-700',
};

const matchTypeLabels: Record<string, string> = {
  email: 'Email match',
  phone: 'Phone match',
  name: 'Name match',
};

export function DedupDialog({ open, onOpenChange, onMergeComplete }: DedupDialogProps) {
  const [groups, setGroups] = useState<CrmDuplicateGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState<string | null>(null);

  const fetchDuplicates = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const result = await api.findDuplicateContacts();
      setGroups(result.groups);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to scan for duplicates',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    fetchDuplicates();
  }, [fetchDuplicates]);

  async function handleMerge(group: CrmDuplicateGroup, primaryId: string) {
    const secondaryIds = group.contacts.filter((c) => c.id !== primaryId).map((c) => c.id);
    setMerging(primaryId);
    try {
      const result = await api.mergeContacts(primaryId, secondaryIds);
      toast({
        title: 'Contacts merged',
        description: `Merged ${result.mergedCount} contacts. Moved ${result.activitiesMoved} activities, ${result.tasksMoved} tasks, ${result.dealsMoved} deals.`,
      });
      // Remove this group from the list
      setGroups((prev) => prev.filter((g) => g !== group));
      onMergeComplete?.();
    } catch {
      toast({ title: 'Error', description: 'Merge failed', variant: 'destructive' });
    } finally {
      setMerging(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Find Duplicate Contacts
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="space-y-4 py-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No duplicates found</p>
            <p className="text-sm mt-1">All contacts appear to be unique</p>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <p className="text-sm text-muted-foreground">
              Found {groups.length} potential duplicate group{groups.length > 1 ? 's' : ''}. Select
              the primary contact to keep for each group.
            </p>

            {groups.map((group, gi) => (
              <Card key={gi}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <Badge variant="secondary" className={confidenceColors[group.confidence]}>
                      {group.confidence} confidence
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {matchTypeLabels[group.matchType]}
                    </span>
                  </div>

                  <div className="space-y-2">
                    {group.contacts.map((contact) => (
                      <div
                        key={contact.id}
                        className="flex items-center justify-between gap-3 p-2 rounded border hover:bg-muted/50"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{contact.name}</p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                            {contact.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {contact.email}
                              </span>
                            )}
                            {contact.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {contact.phone}
                              </span>
                            )}
                            {contact.companyName && <span>{contact.companyName}</span>}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMerge(group, contact.id)}
                          disabled={merging !== null}
                        >
                          <Merge className="h-3.5 w-3.5 mr-1" />
                          {merging === contact.id ? 'Merging...' : 'Keep this'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
